import { normalizeReservationBusinessRules } from './reservationBusinessRules';

export type ReservationTimeLimitReason = 'SAME_DAY_DISABLED' | 'SAME_DAY_CUTOFF_PASSED' | 'MINIMUM_NOTICE_NOT_MET' | 'RESERVATION_START_PASSED' | 'INVALID_TIME_LIMIT_INPUT';
export type ReservationTimeLimitSettings = { businessRules?: unknown; timezone?: string; timeZone?: string };

// Civil dates/cutoff use the unit clock; notice uses elapsed time, including DST.
// Equality is allowed. The caller supplies a trusted, server-owned clock on HTTP paths.
export const reservationTimeLimitReason = (settings: ReservationTimeLimitSettings, start: Date, nowMs: number): ReservationTimeLimitReason | null => {
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(nowMs)) return 'INVALID_TIME_LIMIT_INPUT';
  if (start.getTime() < nowMs) return 'RESERVATION_START_PASSED';
  try {
    const limits = normalizeReservationBusinessRules(settings.businessRules).timeLimits;
    if (!limits.enabled) return null;
    const formatter = new Intl.DateTimeFormat('en-CA', { timeZone: settings.timezone || settings.timeZone || 'Europe/Budapest', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
    const parts = (date: Date) => {
      const p = formatter.formatToParts(date);
      const get = (key: string) => p.find(item => item.type === key)!.value;
      return { day: `${get('year')}-${get('month')}-${get('day')}`, clockMs: (Number(get('hour')) * 3600 + Number(get('minute')) * 60 + Number(get('second'))) * 1000 + date.getUTCMilliseconds() };
    };
    const now = new Date(nowMs), localNow = parts(now);
    if (parts(start).day === localNow.day) {
      if (!limits.sameDayAllowed) return 'SAME_DAY_DISABLED';
      if (limits.sameDayCutoff !== null) {
        const [hour, minute] = limits.sameDayCutoff.split(':').map(Number);
        if (localNow.clockMs > (hour * 60 + minute) * 60_000) return 'SAME_DAY_CUTOFF_PASSED';
      }
    }
    return start.getTime() - nowMs < limits.minimumNoticeHours * 3_600_000 ? 'MINIMUM_NOTICE_NOT_MET' : null;
  } catch { return 'INVALID_TIME_LIMIT_INPUT'; }
};

/**
 * Calendar-only day eligibility. Start-time minimum notice is deliberately NOT
 * a whole-day ban: later times today can still be bookable.
 *
 * Compare civil date keys in the venue's timezone, never the browser timezone.
 * The server's reservationTimeLimitReason remains the final authority.
 */
export type ReservationCalendarDayBlockReason =
  | 'PAST_DAY'
  | 'SAME_DAY_DISABLED'
  | 'SAME_DAY_CUTOFF_PASSED'
  | 'INVALID_TIME_LIMIT_INPUT';

export const reservationCalendarDayBlockReason = (
  settings: ReservationTimeLimitSettings,
  dateKey: string,
  nowMs: number,
): ReservationCalendarDayBlockReason | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey) || !Number.isFinite(nowMs)) return 'INVALID_TIME_LIMIT_INPUT';
  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: settings.timezone || settings.timeZone || 'Europe/Budapest',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    });
    const parts = formatter.formatToParts(new Date(nowMs));
    const part = (key: string) => parts.find(item => item.type === key)!.value;
    const todayKey = `${part('year')}-${part('month')}-${part('day')}`;
    if (dateKey < todayKey) return 'PAST_DAY';
    if (dateKey > todayKey) return null;

    const limits = normalizeReservationBusinessRules(settings.businessRules).timeLimits;
    if (!limits.enabled) return null;
    if (!limits.sameDayAllowed) return 'SAME_DAY_DISABLED';
    if (limits.sameDayCutoff !== null) {
      const [hour, minute] = limits.sameDayCutoff.split(':').map(Number);
      const nowClockMs = (Number(part('hour')) * 3600 + Number(part('minute')) * 60 + Number(part('second'))) * 1000
        + new Date(nowMs).getUTCMilliseconds();
      if (nowClockMs > (hour * 60 + minute) * 60_000) return 'SAME_DAY_CUTOFF_PASSED';
    }
    return null;
  } catch {
    return 'INVALID_TIME_LIMIT_INPUT';
  }
};

export const reservationTimeLimitMessage = (reason: ReservationTimeLimitReason, locale: 'hu' | 'en'): string => ({
  SAME_DAY_DISABLED: { hu: 'Erre a napra már csak közvetlen egyeztetéssel foglalhat. Kérjük, keresse az egységet.', en: 'Same-day online bookings are closed. Please contact the venue.' },
  SAME_DAY_CUTOFF_PASSED: { hu: 'Az aznapi online foglalás határideje lejárt. Keresd az egységet.', en: 'The same-day booking cutoff has passed. Please contact the venue.' },
  MINIMUM_NOTICE_NOT_MET: { hu: 'Ehhez az időponthoz már túl közeli a kezdés. Válasszon későbbi időpontot, vagy keresse az egységet.', en: 'This start time is too soon to book online. Choose a later time or contact the venue.' },
  RESERVATION_START_PASSED: { hu: 'A kiválasztott kezdési időpont már elmúlt.', en: 'The selected start time has already passed.' },
  INVALID_TIME_LIMIT_INPUT: { hu: 'A foglalási időkorlát nem ellenőrizhető. Kérjük, frissítse az oldalt.', en: 'Booking time limits could not be checked. Please refresh the page.' },
}[reason][locale]);

export const reservationTimeLimitsSummary = (settings: ReservationTimeLimitSettings, locale: 'hu' | 'en'): string => {
  try {
    const limits = normalizeReservationBusinessRules(settings.businessRules).timeLimits;
    if (!limits.enabled) return '';
    const parts: string[] = [];
    if (!limits.sameDayAllowed) parts.push(locale === 'hu' ? 'Aznapi online foglalás nem lehetséges.' : 'Same-day online bookings are not available.');
    else if (limits.sameDayCutoff) parts.push(locale === 'hu' ? `Aznapi online foglalás ${limits.sameDayCutoff}-ig.` : `Same-day online bookings until ${limits.sameDayCutoff}.`);
    if (limits.minimumNoticeHours > 0) parts.push(locale === 'hu' ? `Legalább ${limits.minimumNoticeHours} órával a kezdés előtt foglaljon.` : `Book at least ${limits.minimumNoticeHours} hours before arrival.`);
    if (parts.length) parts.push(locale === 'hu' ? 'Az időpontok az egység helyi idejében értendők.' : 'Times are in the venue’s local timezone.');
    return parts.join(' ');
  } catch { return reservationTimeLimitMessage('INVALID_TIME_LIMIT_INPUT', locale); }
};
